<template>
    <div class="video-carousel-container" :style="containerStyle">
        <button class="video-carousel__nav video-carousel__nav--prev" @click="scroll('left')" v-show="canScrollLeft">
            <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
        </button>

        <div class="video-carousel" ref="carouselRef" @scroll="handleScroll">
            <div v-for="story in stories" :key="story.id" class="video-carousel__item"
                :class="{ 'video-carousel__item--focused': focusedStoryId === story.id }" @click="openStory(story)"
                ref="itemRefs">
                <div class="video-carousel__thumbnail">
                    <video :src="getVideoUrl(story.videos?.[0])" muted loop playsinline
                        class="video-carousel__preview-video" :poster="getVideoPoster(story.videos?.[0])"
                        ref="videoRefs">
                    </video>
                    <div class="video-carousel__play-btn">
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </div>
                </div>
                <div class="video-carousel__overlay">
                    <p class="video-carousel__title">{{ story.name }}</p>
                </div>
            </div>
        </div>

        <button class="video-carousel__nav video-carousel__nav--next" @click="scroll('right')" v-show="canScrollRight">
            <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
        </button>

        <PlayerModal v-if="modal.isVisible" :is-visible="modal.isVisible" :stories="stories"
            :initial-story-id="modal.storyId" :autoplay="true" :muted="true" :analytics="analytics"
            @player-close="closePlayer" @video-start="(story, data) => $emit('video-start', story, data)"
            @video-end="(data) => $emit('video-end', data)" @story-change="(story) => $emit('story-change', story)"
            @video-progress="(data) => $emit('video-progress', data)"
            @video-interaction="(data) => $emit('video-interaction', data)" />
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import PlayerModal from './PlayerModal.vue'

const props = defineProps({
    stories: {
        type: Array,
        default: () => []
    },
    analytics: {
        type: Object,
        default: null
    },
    itemsVisible: {
        type: Number,
        default: 5
    },
    autoAdvanceInterval: {
        type: Number,
        default: 6000
    }
})

const emit = defineEmits([
    'video-start',
    'video-end',
    'story-change',
    'video-progress',
    'player-open',
    'player-close',
    'video-interaction'
])

const carouselRef = ref(null)
const itemRefs = ref([])
const videoRefs = ref([])
const canScrollLeft = ref(false)
const canScrollRight = ref(false)
const focusedStoryId = ref(null)
const autoAdvanceTimer = ref(null)

const containerStyle = computed(() => ({
    '--items-visible': props.itemsVisible
}))

const modal = reactive({
    isVisible: false,
    storyId: null
})

const getVideoUrl = (videoData) => {
    if (!videoData) return ''
    if (typeof videoData === 'string') return videoData
    return videoData.url || videoData.attachment || ''
}

const getVideoPoster = (videoData) => {
    if (!videoData) return ''
    if (typeof videoData === 'object') return videoData.thumbnail || videoData.poster || ''
    return ''
}

const handleScroll = () => {
    if (!carouselRef.value) return
    const el = carouselRef.value
    canScrollLeft.value = el.scrollLeft > 10
    canScrollRight.value = el.scrollLeft < (el.scrollWidth - el.clientWidth - 10)

    calculateFocusedItem()
}

const calculateFocusedItem = () => {
    if (!carouselRef.value || !itemRefs.value.length) return

    const carouselRect = carouselRef.value.getBoundingClientRect()
    const carouselCenter = carouselRect.left + carouselRect.width / 2

    let minDistance = Infinity
    let closestId = null

    itemRefs.value.forEach((itemEl, index) => {
        if (!itemEl) return
        const rect = itemEl.getBoundingClientRect()
        const itemCenter = rect.left + rect.width / 2
        const distance = Math.abs(carouselCenter - itemCenter)

        if (distance < minDistance) {
            minDistance = distance
            closestId = props.stories[index].id
        }
    })

    if (closestId !== focusedStoryId.value) {
        focusedStoryId.value = closestId
    }
}

watch(focusedStoryId, (newId) => {
    if (!videoRefs.value.length) return

    videoRefs.value.forEach((videoEl, index) => {
        if (!videoEl) return
        const storyId = props.stories[index] ? props.stories[index].id : null
        if (storyId === newId) {
            videoEl.play().catch(() => {
                console.warn('Autoplay blocked for story:', storyId)
            })
        } else {
            videoEl.pause()
        }
    })
})

const startAutoAdvance = () => {
    stopAutoAdvance()
    if (props.stories.length <= 1) return

    autoAdvanceTimer.value = setInterval(() => {
        scroll('right')
    }, props.autoAdvanceInterval)
}

const stopAutoAdvance = () => {
    if (autoAdvanceTimer.value) {
        clearInterval(autoAdvanceTimer.value)
        autoAdvanceTimer.value = null
    }
}

watch(() => modal.isVisible, (visible) => {
    if (visible) {
        stopAutoAdvance()
    } else {
        startAutoAdvance()
    }
})

const scroll = (direction) => {
    if (!carouselRef.value || !itemRefs.value.length) return

    let currentIndex = props.stories.findIndex(s => s.id === focusedStoryId.value)

    let targetIndex;
    if (currentIndex === -1) {
        targetIndex = direction === 'left' ? props.stories.length - 1 : 0
    } else {
        targetIndex = direction === 'left' ? currentIndex - 1 : currentIndex + 1
    }

    if (targetIndex < 0) {
        targetIndex = props.stories.length - 1
    } else if (targetIndex >= props.stories.length) {
        targetIndex = 0
    }

    const targetEl = itemRefs.value[targetIndex]
    if (targetEl) {
        targetEl.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
        })
    }

    if (autoAdvanceTimer.value) {
        startAutoAdvance()
    }
}

const openStory = (story) => {
    stopAutoAdvance()
    modal.storyId = story.id
    modal.isVisible = true
    if (props.analytics) props.analytics.trackPlayerOpen({ format: 'carousel' })
    emit('player-open')
}

const closePlayer = () => {
    modal.isVisible = false
    if (props.analytics) props.analytics.trackPlayerClose({ format: 'carousel' })
    emit('player-close')
}

onMounted(() => {
    if (props.analytics) {
        props.analytics.trackWidgetLoad({
            format: 'carousel',
            storiesCount: props.stories.length
        })
    }

    setTimeout(() => {
        handleScroll()
        if (!focusedStoryId.value && props.stories.length > 0) {
            focusedStoryId.value = props.stories[0].id
        }
        startAutoAdvance()
    }, 500)
})

import { onUnmounted } from 'vue'
onUnmounted(() => {
    stopAutoAdvance()
})
</script>